import { useState, useEffect } from 'react';
import { graphql } from 'react-relay';
import {
 Popover, PopoverButton, PopoverPanel, Transition,
} from '@headlessui/react';
import { usePopper } from 'react-popper';
import Icon from '@/core/icon.js';
import { Markdown, useTranslation } from '@/helpers/translate';
import { useZusModalStore, ModalWrapper } from '@/helpers/modal.js';
import { useSpacePublicZustandStore } from '@/helpers/spacepublicprovider';

const queryServicesHost = graphql`
  fragment servicesHostSpacePublic_services on SpaceHostServices @relay(plural: true) {
    title
    icon
    description
    free
    price {
      unit
      excl_tax
    }
    score
  }
`;

function ServicesModal({ services, limit = 999 }) {
  const { t } = useTranslation();
  if (!services) return null;
  services.sort((a, b) => -1 * (a.score - b.score));
  return (
    <section className="z-20 flex flex-col justify-center px-6 py-16 bg-white min-h-dvh lg:min-h-fit lg:rounded-2xl md:p-12 text-purple-5/50 md:flex-row lg:h-fit">
      <div>
        <h2 className="w-full pb-4 border-white-dark2 m-0 font-heading text-[14px] font-extrabold uppercase leading-[1.2] tracking-[0.02em] text-was-ink md:text-[15px]">{t('space:equipments')}</h2>
        <div className="grid w-full grid-cols-2 gap-5 md:grid md:gap-8 md:grid-cols-3">
          {services.slice(0, limit).map((service) => (
            <div className="flex flex-row items-center md:mr-6" key={`serviceHostLvl1${service.title}`}>
              <Icon name={service.icon} className="mr-3 fill-current text-purple-5/50 size-6" />
              <div className="flex flex-col text-left">
                <span className="text-sm leading-5 text-purple-5/80">{t(service.title.replace('.', ':'))}</span>
                <span className="font-medium text-[15px] text-black">{service.free ? t('space:title.included') : `${service?.price?.excl_tax}€/${t(`space:formules.${service?.price?.unit}`)}`}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceHostItem({ service }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [refLabel, setRefLabel] = useState();
  const [refPopover, setRefPopover] = useState();

  const { styles, attributes } = usePopper(refLabel, refPopover, { placement: 'top' });
  return (
    <Popover>
      <PopoverButton className="flex flex-row items-center mr-6 text-left" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} ref={setRefLabel}>
        <Icon name={service.icon} className="mr-3 fill-current text-purple-5/80 size-6" />
        <div className="flex flex-col text-left">
          <span className="text-sm leading-5 text-purple-5/60">{t(service.title.replace('.', ':'))}</span>
          <span className="font-medium text-[15px] text-black">{service.free ? t('space:title.included') : `${service?.price?.excl_tax}€/${t(`space:formules.${service?.price?.unit}`)}`}</span>
        </div>
      </PopoverButton>
      {!(service.description)
        || (
        <Transition
          as="div"
          className="relative"
          show={open}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <PopoverPanel
            static
            anchor="bottom start"
            ref={setRefPopover}
            className="px-3 py-2 my-2 text-sm text-center text-white bg-black rounded-lg w-52 shadow-button"
            {...attributes.popper}
          >
            {service.description}
          </PopoverPanel>
        </Transition>
        )}
    </Popover>
  );
}
function ServicesHost({ services, limit = 6 }) {
  const { t } = useTranslation();

  if (!services) return null;

  const { setModal1 } = useZusModalStore.getState();
  services.sort((a, b) => -1 * (a.score - b.score));

  function onClickOpen() {
    setModal1(
      <ModalWrapper onClose={() => setModal1(null)} delay={1}>
        <ServicesModal {...{ services, limit: 999 }} />
      </ModalWrapper>,
    );
  }
  return (
    <section className="flex-col mt-12 md:mt-12 text-purple-5/70 md:flex-row">
      <h2 className="w-full pt-8 pb-5 border-t border-white-dark2 md:pt-12 m-0 font-heading text-[14px] font-extrabold uppercase leading-[1.2] tracking-[0.02em] text-was-ink md:text-[15px]">{t('space:services_title')}</h2>
      <div className="grid grid-cols-2 gap-4 md:grid md:gap-6 md:grid-cols-3 ">
        {services.slice(0, limit).map((service) => <ServiceHostItem {...{ service }} key={`serviceHostLvl0${service.title}`} />)}
      </div>
      {services.length <= limit || <button className="px-3 py-2 mt-4 rounded-lg cursor-pointer text-purple-5 lg:mt-6 bg-purple-0 hover:bg-purple/10" onClick={onClickOpen}><Markdown>{t('btn:see_more_extras', { more: services.length })}</Markdown></button>}
    </section>
  );
}
export { queryServicesHost };

export default ServicesHost;
